import { AttachFile, CloudUpload, Delete, GetApp } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ErrorModal } from "@dexteel/mesf-core";
import { MESApiService } from "@dexteel/mesf-core";
import {
	EntrySubmissionData,
	useEntrySubmission,
} from "../../hooks/useEntrySubmission";
import { LOGBOOK_QUERY_KEYS } from "../../hooks/useLogbookEntries";
import { EntryAttachment } from "../../models/EntryAttachment";
import {
	deleteEntryAttachment,
	getEntryAttachments,
	upsertEntryAttachment,
} from "../../repository/LogbookRepository";
import { DeleteAttachmentConfirmationDialog } from "./DeleteAttachmentConfirmationDialog";

type Props = {
	open: boolean;
	onClose: () => void;
	entryId: number | null;
	isEditing: boolean;
	entryData?: EntrySubmissionData | null;
	onEntrySubmitted?: (entryId: number, entryData?: any) => void;
};

export const AttachmentModal = ({
	open,
	onClose,
	entryId,
	isEditing,
	entryData,
	onEntrySubmitted,
}: Props) => {
	const queryClient = useQueryClient();
	const api = new MESApiService();

	const [error, setError] = useState<string | null>(null);
	const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
	const [currentEntryId, setCurrentEntryId] = useState<number | null>(entryId);
	const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [attachmentToDelete, setAttachmentToDelete] = useState<{
		id: number;
		uploadFileId: number;
		name: string;
	} | null>(null);
	const pendingFilesRef = useRef<File[]>([]);

	// Entry submission hook for background saves
	const { submitEntry, isSubmitLoading } = useEntrySubmission({
		onSuccess: (entry) => {
			setIsSubmittingEntry(false);
			if (entry?.EntryId) {
				setCurrentEntryId(entry.EntryId);
				onEntrySubmitted?.(entry.EntryId, entry);

				// If there are pending files, upload them now with the new entry ID
				if (pendingFilesRef.current.length > 0) {
					const filesToUpload = [...pendingFilesRef.current];
					pendingFilesRef.current = [];
					// Call upload directly with the new entry ID instead of relying on state
					uploadFilesWithEntryId(filesToUpload, entry.EntryId);
				}
			}
		},
		onError: (error) => {
			setIsSubmittingEntry(false);
			setError(error.message);
			pendingFilesRef.current = []; // Clear pending files on error
		},
		isNewEntry: !entryId || entryId <= 0,
	});

	// Sync entryId prop with currentEntryId state
	useEffect(() => {
		setCurrentEntryId(entryId);
	}, [entryId]);

	// Query for fetching attachments
	const {
		data: attachments = [],
		isLoading,
		error: fetchError,
	} = useQuery<EntryAttachment[]>({
		queryKey: ["entryAttachments", currentEntryId],
		queryFn: ({ signal }) =>
			getEntryAttachments({ entryId: currentEntryId! }, signal),
		enabled: !!currentEntryId && currentEntryId > 0,
	});

	// Mutations
	const uploadMutation = useMutation(upsertEntryAttachment, {
		onSuccess: () => {
			queryClient.invalidateQueries(["entryAttachments", currentEntryId]);
			queryClient.invalidateQueries(LOGBOOK_QUERY_KEYS.all);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const deleteMutation = useMutation(
		({
			entryAttachmentId,
			uploadFileId,
		}: {
			entryAttachmentId: number;
			uploadFileId: number;
		}) => deleteEntryAttachment(entryAttachmentId, uploadFileId),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["entryAttachments", currentEntryId]);
				queryClient.invalidateQueries(LOGBOOK_QUERY_KEYS.all);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	// Function to upload files with a specific entry ID (used after background submission)
	const uploadFilesWithEntryId = useCallback(
		async (files: File[], targetEntryId: number) => {
			setUploadingFiles(files);
			setError(null);

			try {
				for (const file of files) {
					// First upload the file using the existing API
					const uploadResponse = await api.uploadFiles([file], "logbook");

					if (uploadResponse?.result?.[0]) {
						const uploadFileId = uploadResponse.result[0].UploadFileId;
						const attachmentURL = uploadResponse.result[0].URL;

						// idempotent: a file already attached (dedup by hash) is a no-op, not an error
						try {
							await uploadMutation.mutateAsync({
								entryId: targetEntryId,
								uploadFileId,
								attachmentName: file.name,
								attachmentURL,
							});
						} catch (err) {
							if (!String(err).includes("already exists")) throw err;
						}

						// Invalidate queries for the specific entry and the main entries lists
						queryClient.invalidateQueries(["entryAttachments", targetEntryId]);
						queryClient.invalidateQueries(LOGBOOK_QUERY_KEYS.all);
					}
				}
			} catch (error) {
				setError(`Failed to upload files: ${error}`);
			} finally {
				setUploadingFiles([]);
			}
		},
		[api, uploadMutation, queryClient],
	);

	const handleFileUpload = useCallback(
		async (files: File[]) => {
			let targetEntryId = currentEntryId;

			// If no entry ID exists and we have entry data, submit the entry first
			if (
				(!targetEntryId || targetEntryId <= 0) &&
				entryData &&
				!isSubmittingEntry
			) {
				try {
					setError(null);
					setIsSubmittingEntry(true);
					// Store files to upload after entry is created
					pendingFilesRef.current = files;
					await submitEntry(entryData);
					// The files will be uploaded in the success callback
					return;
				} catch (error) {
					setIsSubmittingEntry(false);
					setError(`Failed to save entry before uploading files: ${error}`);
					pendingFilesRef.current = [];
					return;
				}
			}

			if (!targetEntryId || targetEntryId <= 0) {
				setError("Entry must be saved before adding attachments");
				return;
			}

			setUploadingFiles(files);
			setError(null);

			try {
				for (const file of files) {
					// First upload the file using the existing API
					const uploadResponse = await api.uploadFiles([file], "logbook");

					if (uploadResponse?.result?.[0]) {
						const uploadFileId = uploadResponse.result[0].UploadFileId;
						const attachmentURL = uploadResponse.result[0].URL;

						// idempotent: a file already attached (dedup by hash) is a no-op, not an error
						try {
							await uploadMutation.mutateAsync({
								entryId: targetEntryId,
								uploadFileId,
								attachmentName: file.name,
								attachmentURL,
							});
						} catch (err) {
							if (!String(err).includes("already exists")) throw err;
						}
					}
				}
				queryClient.invalidateQueries(["entryAttachments", targetEntryId]);
				queryClient.invalidateQueries(LOGBOOK_QUERY_KEYS.all);
			} catch (error) {
				setError(`Failed to upload files: ${error}`);
			} finally {
				setUploadingFiles([]);
			}
		},
		[
			currentEntryId,
			entryData,
			api,
			uploadMutation,
			submitEntry,
			isSubmittingEntry,
		],
	);

	const handleDeleteAttachment = (
		attachmentId: number,
		uploadFileId: number,
		attachmentName: string,
	) => {
		setAttachmentToDelete({
			id: attachmentId,
			uploadFileId,
			name: attachmentName,
		});
		setShowDeleteConfirmation(true);
	};

	const handleDeleteConfirmation = (confirmed: boolean) => {
		setShowDeleteConfirmation(false);
		if (confirmed && attachmentToDelete) {
			deleteMutation.mutate({
				entryAttachmentId: attachmentToDelete.id,
				uploadFileId: attachmentToDelete.uploadFileId,
			});
		}
		setAttachmentToDelete(null);
	};

	// This function is no longer needed as we're using direct anchor links
	// Keeping it as a reference in case it's needed elsewhere
	/*
	const handleDownloadAttachment = (attachment: EntryAttachment) => {
		// Use the URL property with proper encoding
		const encodedUrl = attachment.URL;
		const link = document.createElement("a");
		link.href = encodedUrl;
		link.download = attachment.AttachmentName;
		link.target = "_blank";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	*/

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (isEditing) {
				handleFileUpload(acceptedFiles);
			}
		},
		[handleFileUpload, isEditing],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		disabled: !isEditing,
		multiple: true,
	});

	const isUploading =
		uploadingFiles.length > 0 ||
		uploadMutation.isLoading ||
		isSubmitLoading ||
		isSubmittingEntry;

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				aria-labelledby="attachment-dialog-title"
			>
				<DialogTitle id="attachment-dialog-title">
					Attachments ({attachments.length})
				</DialogTitle>
				<DialogContent dividers>
					{isEditing ? (
						<Box mb={2}>
							<Box
								{...getRootProps()}
								sx={{
									border: (theme) => `2px dashed ${theme.palette.primary.main}`,
									borderRadius: (theme) => theme.shape.borderRadius,
									padding: (theme) => theme.spacing(3),
									textAlign: "center",
									cursor: "pointer",
									marginBottom: (theme) => theme.spacing(2),
									borderColor: (theme) =>
										isDragActive
											? theme.palette.secondary.main
											: theme.palette.primary.main,
									backgroundColor: (theme) =>
										isDragActive
											? theme.palette.action.selected
											: theme.palette.background.default,
									"&:hover": {
										backgroundColor: (theme) => theme.palette.action.hover,
									},
								}}
							>
								<input {...getInputProps()} />
								<CloudUpload
									sx={{
										fontSize: 48,
										color: (theme) => theme.palette.primary.main,
										marginBottom: (theme) => theme.spacing(1),
									}}
								/>
								<Typography variant="h6" gutterBottom>
									{isDragActive
										? "Drop files here..."
										: "Drag & drop files here, or click to select"}
								</Typography>
								<Typography variant="body2" color="textSecondary">
									All file types are supported
								</Typography>
							</Box>
						</Box>
					) : null}

					{!currentEntryId || currentEntryId <= 0 ? (
						isEditing && entryData ? (
							<Typography variant="body1" color="textSecondary" align="center">
								{isSubmitLoading || isSubmittingEntry
									? "Saving entry..."
									: "Upload files to automatically save and attach to this entry"}
							</Typography>
						) : (
							<Typography variant="body1" color="textSecondary" align="center">
								Save the entry first to add attachments
							</Typography>
						)
					) : isLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: 100,
							}}
						>
							<CircularProgress />
						</div>
					) : attachments.length === 0 ? (
						<Typography variant="body1" color="textSecondary" align="center">
							No attachments yet
						</Typography>
					) : (
						<List
							sx={{
								maxHeight: 300,
								overflow: "auto",
							}}
						>
							{attachments.map((attachment) => (
								<ListItem
									key={attachment.EntryAttachmentId}
									sx={{
										border: (theme) => `1px solid ${theme.palette.divider}`,
										borderRadius: (theme) => theme.shape.borderRadius,
										marginBottom: (theme) => theme.spacing(1),
									}}
								>
									<ListItemIcon>
										<AttachFile />
									</ListItemIcon>
									<ListItemText
										primary={attachment.AttachmentName}
										secondary={`Added by ${
											attachment.CreatedBy
										} on ${attachment.CreationTime.toLocaleDateString()}`}
									/>
									<ListItemSecondaryAction>
										<IconButton
											component="a"
											href={attachment.URL}
											target="_blank"
											rel="noopener noreferrer"
											title="Download"
											color="primary"
										>
											<GetApp />
										</IconButton>
										{isEditing && (
											<IconButton
												edge="end"
												onClick={() =>
													handleDeleteAttachment(
														attachment.EntryAttachmentId,
														attachment.UploadFileId,
														attachment.AttachmentName,
													)
												}
												title="Delete"
												color="error"
												disabled={deleteMutation.isLoading}
											>
												<Delete />
											</IconButton>
										)}
									</ListItemSecondaryAction>
								</ListItem>
							))}
						</List>
					)}

					{isUploading && (
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							mt={2}
						>
							<CircularProgress size={20} />
							<Typography variant="body2" style={{ marginLeft: 8 }}>
								Uploading {uploadingFiles.length} file(s)...
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={onClose} variant="contained" color="inherit">
						Close
					</Button>
				</DialogActions>
			</Dialog>
			<DeleteAttachmentConfirmationDialog
				open={showDeleteConfirmation}
				attachmentName={attachmentToDelete?.name || ""}
				onClose={handleDeleteConfirmation}
				isDeleting={deleteMutation.isLoading}
			/>
			<ErrorModal
				error={error || (fetchError as Error)?.message}
				onHide={() => setError(null)}
			/>
		</>
	);
};
